import { useState, useCallback, useRef, type ReactNode } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { XP_REWARDS, LEAGUE_ZONES } from '@tfa/shared'
import { useAuth } from '../hooks/useAuth'
import { useFeatureAccess } from '../hooks/useFeatureAccess'
import { useCurator } from '../contexts/CuratorContext'
import { useTelegram } from '../hooks/useTelegram'
import { api } from '../services/api'
import { getCharacterImage } from '../assets/character'
import { useOverlayPresence } from '../lib/overlayGate'

const F = 'Roboto, sans-serif'
const BG = 'linear-gradient(180deg, #1a1530 0%, #241a3d 55%, #2b1f47 100%)'
const PRIMARY = 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)'

// Фото-отчёт даёт 10 XP (решение владельца; в XP_REWARDS отдельного PHOTO нет).
const PHOTO_XP = 10
// Лиги (порядок снизу вверх) — отображение; источник правды — enum League.
const LEAGUES = ['Новички', 'Бронза', 'Серебро', 'Золото', 'Элита']

function XpRow({ label, xp, note }: { label: string; xp: number; note?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>{label}{note ? <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}> · {note}</span> : null}</span>
      <span style={{ fontSize: 14, fontWeight: 800, color: '#FFD166', whiteSpace: 'nowrap' }}>+{xp} XP</span>
    </div>
  )
}

/**
 * #340/#341: переиспользуемый каркас полноэкранных сторисов — прогресс-сегменты,
 * тап-зоны (лево=назад, право=вперёд), крестик и нижняя кнопка. Контент слайдов
 * приходит пропом. onFirstAction — колбэк первого взаимодействия (для пометки
 * «просмотрено» у одноразовых показов).
 */
export function StoriesShell({
  steps,
  onClose,
  finalLabel,
  onFirstAction,
}: {
  steps: Array<() => ReactNode>
  onClose: () => void
  finalLabel: string
  onFirstAction?: () => void
}) {
  const { impactHaptic } = useTelegram()
  const [step, setStep] = useState(0)
  // Стартовые окна ждут, пока сторис не размонтируются.
  useOverlayPresence('stories')

  const isLast = step >= steps.length - 1
  const go = (dir: 1 | -1) => {
    impactHaptic('light')
    if (dir === 1) { onFirstAction?.(); if (isLast) { onClose() } else setStep((s) => s + 1) }
    else setStep((s) => Math.max(0, s - 1))
  }
  const close = () => { impactHaptic('light'); onFirstAction?.(); onClose() }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: BG, fontFamily: F, display: 'flex', flexDirection: 'column' }}>
      {/* Прогресс-сегменты + закрыть */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 5, flex: 1 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? PRIMARY : 'rgba(255,255,255,0.22)' }} />
          ))}
        </div>
        <button onClick={close} aria-label="Закрыть" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
          <X size={22} color="rgba(255,255,255,0.85)" />
        </button>
      </div>

      {/* Контент + тап-зоны (лево=назад, право=вперёд) */}
      <div style={{ position: 'relative', flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '8px 24px 24px', minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div>{steps[step]()}</div>
        </div>
        <div onClick={() => go(-1)} style={{ position: 'absolute', top: 0, left: 0, width: '33%', height: '100%' }} />
        <div onClick={() => go(1)} style={{ position: 'absolute', top: 0, right: 0, width: '67%', height: '100%' }} />
      </div>

      {/* Нижняя кнопка (дублирует тап вперёд) */}
      <div style={{ padding: '12px 24px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => go(1)}
          style={{ width: '100%', height: 52, borderRadius: 16, border: 'none', background: PRIMARY, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: F }}
        >
          {isLast ? finalLabel : 'Далее'}
        </button>
      </div>
    </div>
  )
}

/**
 * #341: презентация сторисов «как работает геймификация» без гейта — для
 * повторного показа по кнопке в профиле. Одноразовый показ при входе в ACTIVE
 * остаётся в MainModeStories ниже.
 */
export function MainModeStoriesView({ onClose, onFirstAction }: { onClose: () => void; onFirstAction?: () => void }) {
  const { user } = useAuth()
  const u = user as Record<string, unknown> | undefined

  const before = getCharacterImage(1, u?.gender as never)
  const after = getCharacterImage(2, u?.gender as never)

  const STEPS = [
    // ── Сторис 1 — Геймификация ──
    () => (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 16, marginBottom: 18 }}>
          {before && <div style={{ textAlign: 'center' }}><img src={before} alt="" style={{ height: 120, width: 'auto', objectFit: 'contain', opacity: 0.7 }} /><div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>ур. 1</div></div>}
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.6)', paddingBottom: 24 }}>→</div>
          {after && <div style={{ textAlign: 'center' }}><img src={after} alt="" style={{ height: 150, width: 'auto', objectFit: 'contain' }} /><div style={{ fontSize: 11, color: '#A29BFE', fontWeight: 700 }}>ур. 2</div></div>}
        </div>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 8px' }}>Ты в игре!</h1>
        <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.75)', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 }}>
          Выполняй задания, собирай XP, прокачивай уровень и зарабатывай TuCoin.
        </p>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 16, padding: '6px 16px 10px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#A29BFE', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 0 2px' }}>Каждый день</div>
          <XpRow label="Отчёт по питанию" xp={XP_REWARDS.NUTRITION_REPORT.xp} />
          <XpRow label="Сон / шаги / вес" xp={XP_REWARDS.SLEEP.xp + XP_REWARDS.STEPS.xp + XP_REWARDS.WEIGHT.xp} note="по 1 за каждое" />
          <div style={{ fontSize: 11, fontWeight: 800, color: '#A29BFE', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '12px 0 2px' }}>Еженедельно</div>
          <XpRow label="Замеры" xp={XP_REWARDS.MEASUREMENTS.xp} note="1/нед" />
          <XpRow label="Тренировки" xp={XP_REWARDS.TRAINING_VIDEO.xp} note="3/нед" />
          <XpRow label="Техника упражнений" xp={XP_REWARDS.TRAINING_TEXT.xp} note="3/нед" />
          <XpRow label="Кардио 20+ мин" xp={XP_REWARDS.TRAINING_CARDIO.xp} note="2/нед" />
          <XpRow label="Просмотр лекции" xp={XP_REWARDS.LECTURE_WATCHED.xp} />
          <XpRow label="Фото прогресса" xp={PHOTO_XP} note="1/мес" />
        </div>
      </>
    ),
    // ── Сторис 2 — Лиги / рейтинг ──
    () => (
      <>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 8px' }}>Соревнуйся и побеждай</h1>
        <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.75)', textAlign: 'center', margin: '0 0 18px', lineHeight: 1.5 }}>
          Еженедельный рейтинг внутри лиги (комната на 20 человек).
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
          {LEAGUES.map((l, i) => (
            <span key={l} style={{ fontSize: 13, color: i === LEAGUES.length - 1 ? '#FFD166' : 'rgba(255,255,255,0.8)' }}>
              {l}{i < LEAGUES.length - 1 ? <span style={{ color: 'rgba(255,255,255,0.4)' }}> → </span> : ''}
            </span>
          ))}
        </div>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 16, padding: '4px 16px 10px' }}>
          {[
            { z: `Топ ${LEAGUE_ZONES.PROMOTION.min}–${LEAGUE_ZONES.PROMOTION.max}`, t: 'Лига выше +1 TuCoin', c: '#34C759' },
            { z: `${LEAGUE_ZONES.SAFE.min}–${LEAGUE_ZONES.SAFE.max}`, t: 'Остаёшься в лиге', c: 'rgba(255,255,255,0.8)' },
            { z: `${LEAGUE_ZONES.DEMOTION.min}–${LEAGUE_ZONES.DEMOTION.max}`, t: 'Лига ниже', c: '#FF6B6B' },
          ].map((r) => (
            <div key={r.z} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: r.c }}>{r.z}</span>
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>{r.t}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', textAlign: 'center', margin: '16px 0 0', lineHeight: 1.5 }}>
          Еженедельный XP обнуляется в воскресенье, но идёт в прокачку персонажа.
        </p>
      </>
    ),
    // ── Сторис 3 — Стрик (дни в режиме) ──
    () => (
      <>
        {after && <img src={after} alt="" style={{ height: 150, width: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto 16px' }} />}
        <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 8px' }}>Дни в режиме</h1>
        <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.8)', textAlign: 'center', margin: '0 0 14px', lineHeight: 1.55 }}>
          Фиксируешь хотя бы 1 отчёт в день (питание / замеры / тренировка) — день засчитан.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 14 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ width: 28, height: 28, borderRadius: 14, border: '2px solid #FF6B6B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>❤</div>
          ))}
        </div>
        <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', textAlign: 'center', margin: '0 0 8px', lineHeight: 1.55 }}>
          Пропустил день — теряешь 1 жизнь (всего 3). После 3-го пропуска режим обнуляется.
        </p>
        <p style={{ fontSize: 14, color: '#FFD166', textAlign: 'center', fontWeight: 700, margin: 0 }}>
          Чем дольше серия — тем больше TuCoin.
        </p>
      </>
    ),
  ]

  return <StoriesShell steps={STEPS} onClose={onClose} finalLabel="Понятно, в игру!" onFirstAction={onFirstAction} />
}

/**
 * Сторис основного режима (STEP 4). Полноэкранные, листаются тапом, закрываются
 * по тапу/крестику. Показываются ОДИН раз при первом входе в ACTIVE — флаг
 * mainModeStoriesSeen на Client. XP/лиги — из @tfa/shared (XP_REWARDS,
 * LEAGUE_ZONES), не хардкод (кроме PHOTO_XP — решение владельца).
 */
export function MainModeStories() {
  const { user } = useAuth()
  const { isCurator, viewingClientId, isAdminView } = useCurator()
  const queryClient = useQueryClient()
  const [closed, setClosed] = useState(false)
  // #571: сторисы про XP, лиги и серию — только там, где геймификация по тарифу
  // есть. Программе тренировок показывали «Ты в игре!» про баллы, которых у неё нет.
  const gamificationOn = useFeatureAccess().has('gamification')

  const u = user as Record<string, unknown> | undefined
  const mode = u?.mode as string | undefined
  const seen = !!u?.mainModeStoriesSeen
  const completedAt = u?.completedAt

  const markSeen = useMutation({
    mutationFn: () => api.markStoriesSeen({ mainMode: true }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['user', 'me'] }),
  })

  // Помечаем «просмотрено» один раз — при первом действии (а не на маунте, иначе
  // одноразовый показ «съедается» при первом открытии и сторизы пропадают вовсе).
  const markedRef = useRef(false)
  const markOnce = useCallback(() => {
    if (!markedRef.current) { markedRef.current = true; markSeen.mutate() }
  }, [markSeen])

  // Базовая применимость (без учёта «просмотрено»): активный клиент, ACTIVE, не завершён.
  const baseEligible = !!user && !isCurator && !viewingClientId && !isAdminView
    && mode === 'ACTIVE' && !completedAt && gamificationOn
  // Решение «показать в этой сессии» фиксируем на первом применимом рендере, чтобы
  // фоновое проставление флага не схлопнуло уже открытые сторис.
  const showThisSessionRef = useRef<boolean | null>(null)
  if (showThisSessionRef.current === null && baseEligible) showThisSessionRef.current = !seen
  const shouldShow = !closed && baseEligible && showThisSessionRef.current === true

  if (!shouldShow) return null

  return <MainModeStoriesView onClose={() => { markOnce(); setClosed(true) }} onFirstAction={markOnce} />
}
